import React, { useState } from 'react'
import classnames from 'classnames/bind'
import _ from 'lodash'
import type { Listing } from '@adc/sdk/types'
// @ts-ignore no types
import { Text, LabelValue, InfoPill } from '../../../../elementals'
import { PropsOf } from '../../../../../types'
import ExpandableCard from '../../../../layouts/expandableCard'
import CardContainer from '../../../../elementals/cardContainer'
import styles from './styles.scss'

const cx = classnames.bind(styles)
const HEADER_HEIGHT = 24 + 18 * 2 + 18 // content height + padding + bottom margin
const DEFAULT_EXPANDED_HEIGHT = 200

type SubsectionChild = PropsOf<typeof LabelValue> & {
  className?: string
  hide?: boolean
  previousValue?: string | number
}

type Section = {
  title?: string
  children: SubsectionChild[]
  className?: string
}

type WhatChangedSummaryProps = {
  title?: string
  sections: Section[]
  listing?: Listing
  className?: string
  additionalNotes: Section
}

const WhatChangedSummarySection = ({
  sections,
  className,
  additionalNotes,
}: WhatChangedSummaryProps) => {
  const displayChildren = (key: string, children: SubsectionChild[]) => {
    return children.map((c, i) => {
      const hasPreviousValue = c.previousValue !== undefined
      c.level = hasPreviousValue ? 6 : 3
      return (
        <div
          key={`${key}-${i}`}
          style={{ flex: `1 1 calc(50% - 18px)`, wordBreak: 'break-word' }}
        >
          <LabelValue {...c} />
          {hasPreviousValue && (
            <LabelValue
              value={c.previousValue ?? ''}
              label={''}
              level={7}
              isMoneyValue={c.isMoneyValue}
            />
          )}
        </div>
      )
    })
  }

  const [active, setActive] = useState(false)
  const slots = {
    card: ({
      elmId,
      children,
      className,
    }: {
      elmId?: string
      children?: React.ReactNode
      className?: string
    } = {}) => (
      <CardContainer
        elmId={elmId as string}
        className={className as string}
        borderRadius='medium'
        padding='none'
      >
        {children}
      </CardContainer>
    ),
    header: () => (
      <div style={{ width: '100%' }}>
        <Text color='gray' size='large' weight='heavy'>
          {additionalNotes.title}
        </Text>
      </div>
    ),
    content: () => (
      <div
        className={cx('section-children-container', additionalNotes.className)}
      >
        <Text
          color={additionalNotes.children[0].previousValue ? 'success' : 'gray'}
        >
          {additionalNotes.children[0].value}
        </Text>
        {additionalNotes.children[0].previousValue && (
          <Text color='caution' style={{ textDecoration: 'line-through' }}>
            {additionalNotes.children[0].previousValue}
          </Text>
        )}
      </div>
    ),
  }

  return (
    <div className={cx('section-container', className)}>
      {sections.map((s, index) => (
        <div
          key={s.title || `title_${index}`}
          data-elm-id={`${
            s.title?.toLowerCase().replace(/\s/g, '-') || ''
          }-subsection`}
          className={cx('u-mb-6')}
        >
          <Text
            className={cx('u-mb-2')}
            color='gray'
            size='large'
            weight='heavy'
          >
            {s.title}
          </Text>
          <div className={cx('section-children-container', s.className)}>
            {displayChildren(
              s.title ?? '',
              s.children.filter(c => !c.hide)
            )}
          </div>
        </div>
      ))}
      <hr className={cx('line-separator')} />
      <ExpandableCard
        slots={slots}
        active={active}
        onClick={() => setActive(!active)}
        heightHints={{
          collapsed: HEADER_HEIGHT,
          expanded: DEFAULT_EXPANDED_HEIGHT,
        }}
      />
    </div>
  )
}

export default WhatChangedSummarySection
